import { FC, useEffect, useState } from "react";
import classes from './tutorAccruals.module.scss'
import { AccrualItem, ITutorAccrual, ITutorReceiptItem, tutorService } from "../../../entities/tutor";
import { useAppSelector } from "../../../app/store/store";
import { MyError } from "../../../shared/lib/error/MyError";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { FEATURES, hasFeature, useMyActions } from "../../../entities/my";
import { LoaderSpinner } from "../../../shared/ui/spinner";
import { AddReceipt } from "../../../features/addReceipt";
import { Calendar } from "../../../shared/ui/calendar";
import { getDateUTC } from "../../../shared/lib/helpers/getDateUTC";

interface IProps {
    onTrigger?: () => void;
    pendingPayout?: ITutorReceiptItem | null;
    hideTitle?: boolean;
}

const mapPayout = (payout: ITutorReceiptItem): ITutorAccrual => ({
    id: payout.id,
    date: payout.createdAt,
    amount: payout.amount,
    is_receipt: payout.hasReceipt,
    comment: payout.comment,
})

export const TutorAccruals: FC<IProps> = ({onTrigger, pendingPayout, hideTitle = false}) => {

    const {my} = useAppSelector(s => s.myReducer)
    const canReadPayouts = my.role === 'tutor' && hasFeature(my, FEATURES.TUTORS.PAYOUTS_READ, my.id)
    const canCreateReceipt = hasFeature(my, FEATURES.TUTORS.RECEIPTS_CREATE, my.id)
    const [accruals, setAccruals] = useState<ITutorAccrual[] | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()

    useEffect(() => {
        if(!pendingPayout) return

        setAccruals(current => current ?? [mapPayout(pendingPayout)])
    }, [pendingPayout])

    const saveReceipt = (id: string) => () => {
        onTrigger?.()
        setAccruals(current => current?.map(accrual =>
            accrual.id === id
                ? {...accrual, is_receipt: true}
                : accrual
        ) ?? null)
    }

    const getPayments = async (from?: string, to?: string) => {
        if(!from || !to) return
        try{
            setIsLoading(true)
            const payoutsRes = await tutorService.getReceiptList(my.id, from, to)
            setAccruals(payoutsRes.map(mapPayout))
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при получении данных о выплатах исполнителя', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }

    const setDate = (startDate: Date | null, endDate: Date | null) => {
        if(startDate && endDate){
            getPayments(getDateUTC(startDate), getDateUTC(endDate))
        }
        if(!startDate && !endDate){
            setAccruals(null)
        }
    }

    if(!canReadPayouts){
        return null
    }

    return (
        <section className={classes.container}>
            {!hideTitle && <section className={classes.title}>
                Выплаты 
            </section>}
            <Calendar onDateRangeSelect={setDate} />
            {
                isLoading
                    ?
                <section className={classes.loader}>
                    <LoaderSpinner />
                </section>
                    :
                accruals
                    &&
                (
                    accruals.length
                        ?
                    <section className={classes.items}>
                        {accruals.map(accrual =>
                            <AccrualItem 
                                key={accrual.id}    
                                accrual={accrual}
                            >
                                {canCreateReceipt && !accrual.is_receipt && (
                                    <AddReceipt
                                        accrualId={accrual.id}
                                        onSaveTrigger={saveReceipt(accrual.id)}
                                    />
                                )}
                            </AccrualItem>
                        )}
                    </section>
                        :
                    <section className={classes.empty}>Ничего не найдено</section>
                )
            }
        </section>
    )
}
